/**
 * Capabilities and role presets (spec section 2).
 *
 * The spec lists fifteen capabilities. Two administrative capabilities are added so that the
 * People/Access and account-assignment routes can declare a required capability like every other
 * route: `people.manage` (invite, activate, suspend, remove, roles, sessions) and `account.assign`
 * (assign users to accounts). Both belong to the agency_administrator preset only.
 */
import { z } from 'zod';

export const CAPABILITIES = [
  'account.register',
  'account.connect',
  'grc.sync',
  'artifact.create',
  'artifact.export',
  'email.draft',
  'email.send',
  'rumi.query',
  'rumi_code.read',
  'rumi_code.run',
  'rumi_code.approve',
  'rumi_code.configure',
  'template.edit',
  'template.publish',
  'audit.read',
  'audit.export',
  'credential.debug',
  'ai_key.manage',
  'device.manage',
  // administration additions (see file header)
  'people.manage',
  'account.assign',
] as const;
export const CapabilitySchema = z.enum(CAPABILITIES);
export type Capability = z.infer<typeof CapabilitySchema>;

export const ROLES = [
  'agency_administrator',
  'forward_deployed_operator',
  'template_editor',
  'template_publisher',
  'audit_reviewer',
] as const;
export const RoleSchema = z.enum(ROLES);
export type Role = z.infer<typeof RoleSchema>;

export const ROLE_LABEL: Record<Role, string> = {
  agency_administrator: 'Agency administrator',
  forward_deployed_operator: 'Forward Deployed operator',
  template_editor: 'Template editor',
  template_publisher: 'Template publisher',
  audit_reviewer: 'Audit reviewer',
};

/**
 * Default capability set per role. `credential.debug` is an explicit per-user grant.
 *
 * real-providers change: `email.send` is UNUSED. Agency creates Gmail drafts only (the operator
 * reviews and sends from Gmail), so no preset, grant list or helper ever grants it. The enum value
 * stays for schema compatibility with stored entitlements and audit events.
 */
export const ROLE_CAPABILITIES: Record<Role, readonly Capability[]> = {
  agency_administrator: ['people.manage', 'account.assign', 'account.register', 'device.manage', 'ai_key.manage', 'rumi_code.read', 'rumi_code.run', 'rumi_code.approve', 'rumi_code.configure'],
  forward_deployed_operator: [
    'account.connect',
    'grc.sync',
    'artifact.create',
    'artifact.export',
    'email.draft',
    'rumi.query',
    'rumi_code.read',
    'rumi_code.run',
  ],
  template_editor: ['template.edit'],
  template_publisher: ['template.edit', 'template.publish'],
  audit_reviewer: ['audit.read', 'audit.export'],
};

/** Capabilities that are never implied by a role and must be granted per user (real-providers change: email.send is not grantable). */
export const GRANT_ONLY_CAPABILITIES: readonly Capability[] = ['credential.debug'];

/**
 * Desktop builds parse the entitlement with a strict enum of CAPABILITIES, so a capability must not
 * be issued to an app version that predates it: the lease would be rejected as malformed and the
 * device would lock until it updated. The server filters by this map; a capability absent from it
 * has existed since the first release. Add an entry whenever CAPABILITIES grows.
 */
export const CAPABILITY_MIN_APP_VERSION: Readonly<Partial<Record<Capability, string>>> = {
  'rumi_code.read': '1.0.16',
  'rumi_code.run': '1.0.16',
  'rumi_code.approve': '1.0.16',
  'rumi_code.configure': '1.0.16',
};

export function hasCapability(caps: Iterable<Capability>, cap: Capability): boolean {
  for (const c of caps) if (c === cap) return true;
  return false;
}

export function hasAnyCapability(caps: Iterable<Capability>, required: Capability | readonly Capability[]): boolean {
  const list = Array.isArray(required) ? (required as readonly Capability[]) : [required as Capability];
  if (list.length === 0) return true;
  const set = new Set(caps);
  return list.some((c) => set.has(c));
}

/** Sorted, de-duplicated union of the preset capabilities of `roles`. */
export function capabilitiesForRoles(roles: Iterable<Role>): Capability[] {
  const set = new Set<Capability>();
  for (const role of roles) for (const cap of ROLE_CAPABILITIES[role]) set.add(cap);
  return sortCapabilities(set);
}

/**
 * Roles plus explicit grants (credential.debug or any other per-user grant).
 * real-providers change: `options.directSendEnabled` is accepted for signature compatibility and
 * ignored; `email.send` is never granted, and a stored grant of it is dropped.
 */
export function effectiveCapabilities(
  roles: Iterable<Role>,
  grants: Iterable<Capability> = [],
  _options: { directSendEnabled?: boolean } = {},
): Capability[] {
  const set = new Set<Capability>(capabilitiesForRoles(roles));
  for (const g of grants) if (g !== 'email.send') set.add(g);
  return sortCapabilities(set);
}

export function sortCapabilities(caps: Iterable<Capability>): Capability[] {
  const order = new Map<Capability, number>(CAPABILITIES.map((c, i) => [c, i]));
  return Array.from(new Set(caps)).sort((a, b) => (order.get(a) ?? 0) - (order.get(b) ?? 0));
}

/** True when `roles` contains a role whose preset includes `cap`. */
export function roleGrants(role: Role, cap: Capability): boolean {
  return ROLE_CAPABILITIES[role].includes(cap);
}
